/**
 * Draft cards shared by the three Search build screens (campaign workspace,
 * add ad groups, add ads) so they cannot drift: an ad-group card (name, max
 * CPC, keywords, negatives, its ads) and an ad card (RSA editor with strength,
 * publish state and duplicate / remove). Module scope, like every editor here.
 */
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Copy, FileText, Layers, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Tag } from '@/components/google-ads/youtube/youtubeShared';
import { Pub, StepIcon } from '@/components/google-ads/objectives/objectiveShared';
import {
  adStrength, cloneRsa, groupSummary, KeywordDraft, newRsa, RsaDraft, RsaEditor, rsaIssues, SearchGroupDraft, SearchGroupEditor,
  searchGroupIssues, StrengthPill,
} from './searchShared';

const IconBtn = ({ title, onClick, disabled, danger, children, small }: {
  title: string; onClick: () => void; disabled?: boolean; danger?: boolean; small?: boolean; children: ReactNode;
}) => (
  <button type="button" title={title} aria-label={title} disabled={disabled} onClick={onClick}
    className={clsx('flex items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt disabled:opacity-40',
      small ? 'h-6 w-6' : 'h-7 w-7', danger ? 'hover:text-danger' : 'hover:text-text-primary')}>
    {children}
  </button>
);

export function AdDraftCard({ ad, index, keywords, focused, onFocus, onChange, onDuplicate, onRemove, made, state, disabled, onWrite, writing, aiFilled }: {
  ad: RsaDraft; index: number; keywords: KeywordDraft[]; focused: boolean; onFocus: () => void;
  onChange: (p: Partial<RsaDraft>) => void; onDuplicate?: () => void; onRemove?: () => void;
  made: boolean; state?: Pub; disabled?: boolean; onWrite?: () => void; writing?: boolean; aiFilled?: boolean;
}) {
  const issues = rsaIssues(ad);
  const started = ad.headlines.some((h) => h.trim()) || ad.descriptions.some((d) => d.trim());
  return (
    <div onFocusCapture={onFocus} className={clsx('rounded-xl border p-3.5 transition-colors', focused ? 'border-primary/40 bg-primary-soft/30' : 'border-border bg-surface')}>
      <div className="mb-3 flex items-center gap-2">
        {state && state !== 'idle' ? <StepIcon s={state} /> : <FileText size={14} className="shrink-0 text-text-muted" />}
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-text-primary">{ad.name.trim() || `Ad ${index + 1}`}</span>
        <StrengthPill s={adStrength(ad, keywords)} className="hidden sm:inline-flex" />
        {made ? <Tag tone="ok">created</Tag> : issues.length ? <span title={`Needs ${issues.join(' · ')}`}><Tag tone="warn">{issues.length} to fix</Tag></span> : <Tag tone="ok">ready</Tag>}
        <Button variant="ghost" size="sm" className="h-6 px-2 text-2xs" onClick={onFocus}>Preview</Button>
        {onDuplicate && <IconBtn small title="Duplicate ad" onClick={onDuplicate} disabled={disabled}><Copy size={13} /></IconBtn>}
        {onRemove && !made && <IconBtn small danger title="Remove ad" onClick={onRemove} disabled={disabled}><Trash2 size={13} /></IconBtn>}
      </div>
      {!made && started && issues.length > 0 && <p className="-mt-1.5 mb-3 text-2xs text-warning">Needs {issues.join(' · ')}.</p>}
      <RsaEditor ad={ad} onChange={onChange} disabled={made || disabled} onWrite={onWrite} writing={writing} aiFilled={aiFilled} />
    </div>
  );
}

export interface GroupDraftCardProps {
  group: SearchGroupDraft; index: number; open: boolean; onToggle: () => void;
  manualCpc: boolean; currency?: string | null;
  pub: Record<string, Pub>; madeGroup: boolean; madeAd: (adId: string) => boolean; busy: boolean;
  /** Group-level patch — ads are patched through it too. */
  onChange: (p: Partial<SearchGroupDraft>) => void;
  onDuplicate?: () => void; onRemove?: () => void;
  activeAdId?: string; onFocusAd: (adId: string) => void;
  /** Landing page for a new ad when the group has none yet. */
  defaultUrl: string;
  onSuggest?: () => void; suggesting?: boolean;
  onWriteAd?: (ad: RsaDraft) => void; writingAd?: (adId: string) => boolean;
  aiAds?: Set<string>; onAdCopyEdited?: (adId: string) => void;
  /** false on the "add ad groups" page variant that creates groups without ads. */
  withAds?: boolean;
}

export function GroupDraftCard(p: GroupDraftCardProps) {
  const { group: g, index, open, manualCpc, madeGroup, busy, withAds = true } = p;
  const bad = searchGroupIssues(g, manualCpc, withAds).length > 0 || (withAds && g.ads.some((a) => rsaIssues(a).length > 0));
  const patchAd = (aid: string, patch: Partial<RsaDraft>) => {
    p.onChange({ ads: g.ads.map((a) => (a.id === aid ? { ...a, ...patch } : a)) });
    if (patch.headlines || patch.descriptions) p.onAdCopyEdited?.(aid);
  };
  return (
    <Card padded={false} className="overflow-hidden">
      <div className={clsx('flex items-center gap-2.5 px-4 py-3', open && 'border-b border-border')}>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left" onClick={p.onToggle} aria-expanded={open}>
          {p.pub[g.id] && p.pub[g.id] !== 'idle' ? <StepIcon s={p.pub[g.id]} />
            : open ? <ChevronDown size={15} className="shrink-0 text-text-muted" /> : <ChevronRight size={15} className="shrink-0 text-text-muted" />}
          <Layers size={15} className="shrink-0 text-text-muted" />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{g.name.trim() || `Ad group ${index + 1}`}</div>
            <div className="truncate text-2xs text-text-muted">{withAds ? groupSummary(g) : groupSummary(g).replace(/ · \d+ ads?$/, '')}</div>
          </div>
        </button>
        {madeGroup ? <Tag tone="ok">created</Tag> : bad ? <Tag tone="warn">incomplete</Tag> : <Tag tone="ok">ready</Tag>}
        {p.onDuplicate && <IconBtn title="Duplicate ad group" onClick={p.onDuplicate} disabled={busy}><Copy size={14} /></IconBtn>}
        {p.onRemove && !madeGroup && <IconBtn danger title="Remove ad group" onClick={p.onRemove} disabled={busy}><Trash2 size={14} /></IconBtn>}
      </div>

      {open && (
        <div className="space-y-4 p-4">
          <SearchGroupEditor group={g} onChange={p.onChange} manualCpc={manualCpc} disabled={madeGroup} currency={p.currency}
            onSuggest={p.onSuggest} suggesting={p.suggesting} />

          {withAds && (
            <div className="space-y-3">
              <div className="text-xs font-semibold text-text-primary">Ads in this group</div>
              {g.ads.map((a, k) => (
                <AdDraftCard key={a.id} ad={a} index={k} keywords={g.keywords} focused={p.activeAdId === a.id} onFocus={() => p.onFocusAd(a.id)}
                  onChange={(patch) => patchAd(a.id, patch)} made={p.madeAd(a.id)} state={p.pub[a.id]} disabled={busy}
                  onDuplicate={() => { const c = cloneRsa(a); p.onChange({ ads: [...g.ads.slice(0, k + 1), c, ...g.ads.slice(k + 1)] }); p.onFocusAd(c.id); }}
                  onRemove={g.ads.length > 1 ? () => p.onChange({ ads: g.ads.filter((x) => x.id !== a.id) }) : undefined}
                  onWrite={p.onWriteAd ? () => p.onWriteAd!(a) : undefined} writing={p.writingAd?.(a.id)} aiFilled={p.aiAds?.has(a.id)} />
              ))}
              <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} disabled={busy}
                onClick={() => {
                  const a = newRsa(g.ads.length + 1, g.ads[g.ads.length - 1]?.finalUrl || p.defaultUrl);
                  p.onChange({ ads: [...g.ads, a] }); p.onFocusAd(a.id);
                }}>
                Add another ad
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
